/**
 * 技能库管理页面
 * 全局技能 CRUD：浏览、新建、编辑、删除技能 + 上传脚本文件
 */

import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import {
  Plus,
  Search,
  Trash2,
  Loader2,
  ChevronRight,
  Zap,
  FileCode,
  Tag,
  Edit,
  Upload,
  ArrowLeft,
} from "lucide-react"
import { Button } from "@/shared/components/ui/button"
import { Card, CardContent } from "@/shared/components/ui/card"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import { Badge } from "@/shared/components/ui/badge"
import { Textarea } from "@/shared/components/ui/textarea"
import { Switch } from "@/shared/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible"
import { SkillApiService, searchClawHubSkills } from "@/modules/agent/services/skill-api"
import { useToast } from "@/shared/hooks/useToast"
import { ToastContainer } from "@/shared/components/ui/toast"
import type {
  SkillInfo,
  SkillDetail,
  SkillType,
  CreateSkillRequest,
  SkillExecutionConfig,
} from "@/modules/agent/types/skill"

// 空的创建请求模板
const EMPTY_SKILL_FORM: CreateSkillRequest = {
  name: '',
  description: '',
  tags: [],
  tool_id: '',
  execution: undefined,
  body: '',
}

const EMPTY_EXECUTION: SkillExecutionConfig = {
  type: 'sandbox',
  runtime: 'python3.11',
  entry: 'run.py',
  requirements: [],
  timeout: 30,
  network: true,
}

export default function SkillManagement() {
  const navigate = useNavigate()
  const { toasts, showError, showSuccess, removeToast } = useToast()

  // 列表状态
  const [skills, setSkills] = useState<SkillInfo[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [typeFilter, setTypeFilter] = useState<SkillType | "all">("all")

  // 展开详情
  const [expandedSkills, setExpandedSkills] = useState<Set<string>>(new Set())
  const [skillDetails, setSkillDetails] = useState<Record<string, SkillDetail>>({})
  const [loadingDetails, setLoadingDetails] = useState<Set<string>>(new Set())

  // 创建/编辑对话框
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState<CreateSkillRequest>({ ...EMPTY_SKILL_FORM })
  const [formSkillType, setFormSkillType] = useState<SkillType>('prompt')
  const [isSaving, setIsSaving] = useState(false)
  const [tagsInput, setTagsInput] = useState('')

  // 脚本上传对话框
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false)
  const [uploadSkillName, setUploadSkillName] = useState('')
  const [uploadFilename, setUploadFilename] = useState('')
  const [uploadContent, setUploadContent] = useState('')
  const [isUploading, setIsUploading] = useState(false)

  // 删除确认
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [skillToDelete, setSkillToDelete] = useState<SkillInfo | null>(null)

  // ClawHub 社区技能状态
  const [clawhubQuery, setClawhubQuery] = useState("")
  const [clawhubSkills, setClawhubSkills] = useState<any[]>([])
  const [clawhubLoading, setClawhubLoading] = useState(false)
  const [installedHermes, setInstalledHermes] = useState<any[]>([])
  const [installingSkill, setInstallingSkill] = useState("")
  const [activeTab, setActiveTab] = useState<"axiom" | "clawhub">("axiom")

  // ====== 数据加载 ======

  const loadSkills = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await SkillApiService.getSkillLibrary()
      if (response.success) {
        setSkills(response.data.skills || [])
      }
    } catch (error: unknown) {
      console.error('加载技能列表失败:', error)
      showError('加载技能列表失败')
    } finally {
      setIsLoading(false)
    }
  }, [showError])

  useEffect(() => {
    loadSkills()
  }, [loadSkills])

  const loadSkillDetail = useCallback(async (name: string) => {
    if (skillDetails[name] || loadingDetails.has(name)) return
    setLoadingDetails(prev => new Set(prev).add(name))
    try {
      const response = await SkillApiService.getSkillDetail(name)
      if (response.success) {
        setSkillDetails(prev => ({ ...prev, [name]: response.data }))
      }
    } catch (error: unknown) {
      console.error(`加载技能详情失败: ${name}`, error)
    } finally {
      setLoadingDetails(prev => {
        const next = new Set(prev)
        next.delete(name)
        return next
      })
    }
  }, [skillDetails, loadingDetails])

  // ====== ClawHub 操作 ======

  const searchClawhub = useCallback(async (q = "") => {
    setClawhubLoading(true)
    try {
      const skills = await searchClawHubSkills(q)
      setClawhubSkills(skills || [])
    } catch { showError("搜索 ClawHub 失败") }
    finally { setClawhubLoading(false) }
  }, [showError])

  const loadInstalledHermes = useCallback(async () => {
    try {
      const r = await SkillApiService.getInstalledClawhub()
      if (r.success) setInstalledHermes(r.data.skills || [])
    } catch { /* silent */ }
  }, [])

  const installHermesSkill = async (name: string) => {
    setInstallingSkill(name)
    try {
      const r = await SkillApiService.installFromClawhub(name)
      if (r.success) {
        showSuccess(`技能 ${name} 安装成功${r.data.cached ? " (已缓存)" : ""}`)
        loadInstalledHermes()
      } else showError(`安装失败: ${(r as any).message || 'unknown'}`)
    } catch { showError(`安装 ${name} 失败`) }
    finally { setInstallingSkill("") }
  }

  const deleteHermesSkill = async (name: string) => {
    try {
      await SkillApiService.deleteFromClawhub(name)
      showSuccess(`已删除 ${name}`)
      loadInstalledHermes()
    } catch { showError(`删除 ${name} 失败`) }
  }

  // 初始化：默认加载 ClawHub trending
  useEffect(() => { loadInstalledHermes(); searchClawhub("") }, [loadInstalledHermes, searchClawhub])

  // ====== 筛选 ======

  const filteredSkills = skills.filter(s => {
    if (typeFilter !== 'all' && s.type !== typeFilter) return false
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      return (
        s.name.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q) ||
        s.tags.some(t => t.toLowerCase().includes(q))
      )
    }
    return true
  })

  // ====== 展开/折叠 ======

  const toggleExpand = (name: string) => {
    setExpandedSkills(prev => {
      const next = new Set(prev)
      if (next.has(name)) {
        next.delete(name)
      } else {
        next.add(name)
        loadSkillDetail(name)
      }
      return next
    })
  }

  // ====== 新建 ======

  const openCreateDialog = () => {
    setIsEditing(false)
    setFormData({ ...EMPTY_SKILL_FORM })
    setFormSkillType('prompt')
    setTagsInput('')
    setIsFormDialogOpen(true)
  }

  // ====== 编辑 ======

  const openEditDialog = async (skill: SkillInfo) => {
    setIsEditing(true)
    // 加载详情
    let detail = skillDetails[skill.name]
    if (!detail) {
      try {
        const resp = await SkillApiService.getSkillDetail(skill.name)
        if (resp.success) {
          detail = resp.data
          setSkillDetails(prev => ({ ...prev, [skill.name]: detail! }))
        }
      } catch {
        showError('加载技能详情失败')
        return
      }
    }
    if (!detail) return
    setFormData({
      name: detail.name,
      description: detail.description,
      tags: detail.tags,
      tool_id: detail.tool_id || '',
      execution: detail.execution || undefined,
      body: detail.body || '',
    })
    setFormSkillType(detail.type)
    setTagsInput(detail.tags.join(', '))
    setIsFormDialogOpen(true)
  }

  // ====== 保存 ======

  const handleSave = async () => {
    if (!formData.name || !formData.description) {
      showError('请填写技能名称和描述')
      return
    }
    // 名称校验：只允许字母数字下划线连字符，必须以字母开头
    if (!/^[a-zA-Z][a-zA-Z0-9_-]*$/.test(formData.name)) {
      showError('技能名称只允许字母、数字、下划线、连字符，且必须以字母开头')
      return
    }
    setIsSaving(true)
    try {
      const tags = tagsInput
        .split(',')
        .map(t => t.trim())
        .filter(Boolean)
      const payload: CreateSkillRequest = {
        ...formData,
        tags,
        execution: formSkillType === 'sandbox'
          ? (formData.execution || { ...EMPTY_EXECUTION })
          : undefined,
      }
      const resp = await SkillApiService.createOrUpdateSkill(formData.name, payload)
      if (resp.success) {
        showSuccess(isEditing ? '技能已更新' : '技能已创建')
        setIsFormDialogOpen(false)
        // 更新详情缓存
        setSkillDetails(prev => ({ ...prev, [formData.name]: resp.data }))
        await loadSkills()
      }
    } catch (error: unknown) {
      console.error('保存技能失败:', error)
      showError('保存技能失败')
    } finally {
      setIsSaving(false)
    }
  }

  // ====== 删除 ======

  const handleDelete = async () => {
    if (!skillToDelete) return
    try {
      await SkillApiService.deleteSkill(skillToDelete.name)
      showSuccess(`技能 "${skillToDelete.name}" 已删除`)
      setIsDeleteDialogOpen(false)
      setSkillToDelete(null)
      // 清理详情缓存
      setSkillDetails(prev => {
        const next = { ...prev }
        delete next[skillToDelete.name]
        return next
      })
      setExpandedSkills(prev => {
        const next = new Set(prev)
        next.delete(skillToDelete.name)
        return next
      })
      await loadSkills()
    } catch (error: unknown) {
      console.error('删除技能失败:', error)
      showError('删除技能失败')
    }
  }

  // ====== 文件上传 ======

  const openUploadDialog = (skillName: string) => {
    setUploadSkillName(skillName)
    setUploadFilename('')
    setUploadContent('')
    setIsUploadDialogOpen(true)
  }

  const handleUploadFile = async () => {
    if (!uploadFilename || !uploadContent) {
      showError('请填写文件名和文件内容')
      return
    }
    setIsUploading(true)
    try {
      await SkillApiService.uploadSkillFile(uploadSkillName, uploadFilename, uploadContent)
      showSuccess(`文件 "${uploadFilename}" 上传成功`)
      setIsUploadDialogOpen(false)
      // 刷新详情和列表
      setSkillDetails(prev => {
        const next = { ...prev }
        delete next[uploadSkillName]
        return next
      })
      loadSkillDetail(uploadSkillName)
      await loadSkills()
    } catch (error: unknown) {
      console.error('上传文件失败:', error)
      showError('上传文件失败')
    } finally {
      setIsUploading(false)
    }
  }

  // ====== 渲染 ======

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* 顶部导航栏 */}
      <header className="border-b bg-white dark:bg-slate-900 sticky top-0 z-10">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              返回
            </Button>
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-500" />
              <h1 className="text-xl font-bold">技能管理</h1>
            </div>
          </div>
        </div>
      </header>

      {/* 主内容区 */}
      <main className="container mx-auto px-4 py-8">
        {/* 工具栏 */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            {/* 搜索 */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="搜索技能..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-64"
              />
            </div>
            {/* 类型筛选 */}
            <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as SkillType | "all")}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="全部类型" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部类型</SelectItem>
                <SelectItem value="prompt">Prompt 技能</SelectItem>
                <SelectItem value="sandbox">Sandbox 技能</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={openCreateDialog} className="gap-2">
            <Plus className="h-4 w-4" />
            新建技能
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "axiom" | "clawhub")} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="axiom">Axiom 技能库</TabsTrigger>
            <TabsTrigger value="clawhub">ClawHub 社区</TabsTrigger>
          </TabsList>

          <TabsContent value="axiom">
            {/* 技能列表 */}
            {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredSkills.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Zap className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">
                {searchQuery || typeFilter !== 'all'
                  ? '没有找到匹配的技能'
                  : '还没有技能，点击"新建技能"开始'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredSkills.map((skill) => (
              <Collapsible
                key={skill.name}
                open={expandedSkills.has(skill.name)}
                onOpenChange={() => toggleExpand(skill.name)}
                className="group"
              >
                <Card>
                  <CollapsibleTrigger className="w-full">
                    <CardContent className="py-4 px-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <ChevronRight className="h-4 w-4 text-slate-400 transition-transform duration-200 group-data-[state=open]:rotate-90" />
                          <div className="text-left">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{skill.name}</span>
                              <Badge variant={skill.type === 'sandbox' ? 'default' : 'secondary'} className="text-xs">
                                {skill.type === 'sandbox' ? 'Sandbox' : 'Prompt'}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-500 mt-0.5">{skill.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          {skill.tags.length > 0 && (
                            <div className="flex gap-1 mr-2">
                              {skill.tags.slice(0, 3).map((tag) => (
                                <Badge key={tag} variant="outline" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                              {skill.tags.length > 3 && (
                                <Badge variant="outline" className="text-xs">+{skill.tags.length - 3}</Badge>
                              )}
                            </div>
                          )}
                          {skill.type === 'sandbox' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => openUploadDialog(skill.name)}
                              title="上传脚本"
                            >
                              <Upload className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => openEditDialog(skill)}
                            title="编辑"
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
                            onClick={() => {
                              setSkillToDelete(skill)
                              setIsDeleteDialogOpen(true)
                            }}
                            title="删除"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="px-5 pb-4 pt-0 border-t">
                      {loadingDetails.has(skill.name) ? (
                        <div className="flex items-center justify-center py-6">
                          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                        </div>
                      ) : skillDetails[skill.name] ? (
                        <div className="space-y-4 pt-4">
                          {/* 基本信息 */}
                          {skillDetails[skill.name].tool_id && (
                            <div className="flex items-center gap-2 text-sm">
                              <Tag className="h-3.5 w-3.5 text-slate-400" />
                              <span className="text-slate-500">关联工具:</span>
                              <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">
                                {skillDetails[skill.name].tool_id}
                              </code>
                            </div>
                          )}

                          {/* Sandbox 执行配置 */}
                          {skillDetails[skill.name].execution && (
                            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 space-y-1">
                              <p className="text-sm font-medium mb-2">执行配置</p>
                              <div className="grid grid-cols-3 gap-2 text-xs text-slate-600">
                                <div>运行时: <code>{skillDetails[skill.name].execution!.runtime}</code></div>
                                <div>入口: <code>{skillDetails[skill.name].execution!.entry}</code></div>
                                <div>超时: {skillDetails[skill.name].execution!.timeout}s</div>
                                <div>网络: {skillDetails[skill.name].execution!.network ? '允许' : '禁止'}</div>
                                {skillDetails[skill.name].execution!.requirements && skillDetails[skill.name].execution!.requirements!.length > 0 && (
                                  <div className="col-span-2">
                                    依赖: {skillDetails[skill.name].execution!.requirements!.join(', ')}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* 文件列表 */}
                          {skill.files.length > 0 && (
                            <div>
                              <p className="text-sm font-medium mb-1">文件</p>
                              <div className="flex flex-wrap gap-2">
                                {skill.files.map((f) => (
                                  <Badge key={f} variant="outline" className="gap-1 text-xs">
                                    <FileCode className="h-3 w-3" />
                                    {f}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* 正文 */}
                          {skillDetails[skill.name].body && (
                            <div>
                              <p className="text-sm font-medium mb-1">说明</p>
                              <pre className="text-xs text-slate-600 bg-slate-50 dark:bg-slate-800 rounded-lg p-3 whitespace-pre-wrap max-h-48 overflow-y-auto">
                                {skillDetails[skill.name].body}
                              </pre>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400 py-4">加载失败</p>
                      )}
                    </div>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            ))}
          </div>
        )}
          </TabsContent>

          <TabsContent value="clawhub">
      {/* ── ClawHub 社区技能 ── */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Zap className="h-5 w-5 text-purple-500" />
          ClawHub 社区技能
        </h2>

        {/* 搜索 */}
        <div className="flex gap-2 mb-4">
          <Input
            value={clawhubQuery}
            onChange={(e) => setClawhubQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && searchClawhub(clawhubQuery)}
            placeholder="搜索 ClawHub 技能..."
            className="max-w-sm"
          />
          <Button size="sm" onClick={() => searchClawhub(clawhubQuery)} disabled={clawhubLoading}>
            {clawhubLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </div>

        {/* 搜索结果 */}
        {clawhubSkills.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            {clawhubSkills.map((s: any) => (
              <Card key={s.name} className="border-purple-200">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-sm">{s.name}</h3>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">{s.description}</p>
                      {s.tags && (
                        <div className="flex gap-1 mt-2 flex-wrap">
                          {s.tags.slice(0, 3).map((t: string) => (
                            <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-purple-300 text-purple-600 hover:bg-purple-50 shrink-0 ml-2"
                      disabled={installingSkill === s.name}
                      onClick={() => installHermesSkill(s.name)}
                    >
                      {installingSkill === s.name
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : "安装"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* 已安装列表 */}
        {installedHermes.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-2">
              已安装 ({installedHermes.length})
            </h3>
            <div className="space-y-2">
              {installedHermes.map((s: any) => (
                <div key={s.name} className="flex items-center justify-between px-3 py-2 bg-purple-50 rounded-md">
                  <div>
                    <span className="text-sm font-medium">{s.name}</span>
                    {s.description && (
                      <span className="text-xs text-slate-500 ml-2">{s.description}</span>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-400 hover:text-red-600 h-7"
                    onClick={() => deleteHermesSkill(s.name)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
          </TabsContent>
        </Tabs>

      </main>

      {/* 新建/编辑技能对话框 */}
      <Dialog open={isFormDialogOpen} onOpenChange={setIsFormDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{isEditing ? '编辑技能' : '新建技能'}</DialogTitle>
            <DialogDescription>
              {isEditing ? '修改技能配置' : '创建一个新的技能，可以是 Prompt 描述或 Sandbox 可执行脚本'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* 名称 */}
            <div className="space-y-1.5">
              <Label>技能名称 *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="如 analyze_data（字母开头，字母数字下划线连字符）"
                disabled={isEditing}
              />
            </div>

            {/* 描述 */}
            <div className="space-y-1.5">
              <Label>技能描述 *</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="一句话说明这个技能做什么"
              />
            </div>

            {/* 类型 */}
            <div className="space-y-1.5">
              <Label>技能类型</Label>
              <Select
                value={formSkillType}
                onValueChange={(v) => {
                  setFormSkillType(v as SkillType)
                  if (v === 'sandbox' && !formData.execution) {
                    setFormData(prev => ({ ...prev, execution: { ...EMPTY_EXECUTION } }))
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="prompt">Prompt 技能（仅描述注入 Prompt）</SelectItem>
                  <SelectItem value="sandbox">Sandbox 技能（含可执行脚本）</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* 标签 */}
            <div className="space-y-1.5">
              <Label>标签（逗号分隔）</Label>
              <Input
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="如 data, analysis, ui"
              />
            </div>

            {/* 关联工具 ID */}
            <div className="space-y-1.5">
              <Label>关联工具 ID（可选）</Label>
              <Input
                value={formData.tool_id || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, tool_id: e.target.value }))}
                placeholder="MCP 工具路由用"
              />
            </div>

            {/* Sandbox 执行配置 */}
            {formSkillType === 'sandbox' && (
              <div className="border rounded-lg p-3 space-y-3 bg-slate-50 dark:bg-slate-800">
                <p className="text-sm font-medium">Sandbox 执行配置</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">运行时</Label>
                    <Input
                      value={formData.execution?.runtime || 'python3.11'}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        execution: { ...prev.execution || EMPTY_EXECUTION, runtime: e.target.value },
                      }))}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">入口脚本</Label>
                    <Input
                      value={formData.execution?.entry || 'run.py'}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        execution: { ...prev.execution || EMPTY_EXECUTION, entry: e.target.value },
                      }))}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">超时（秒）</Label>
                    <Input
                      type="number"
                      value={formData.execution?.timeout ?? 30}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        execution: { ...prev.execution || EMPTY_EXECUTION, timeout: Number(e.target.value) },
                      }))}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="flex items-center justify-between pt-4">
                    <Label className="text-xs">允许网络</Label>
                    <Switch
                      checked={formData.execution?.network ?? true}
                      onCheckedChange={(checked) => setFormData(prev => ({
                        ...prev,
                        execution: { ...prev.execution || EMPTY_EXECUTION, network: checked },
                      }))}
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">pip 依赖（逗号分隔）</Label>
                  <Input
                    value={formData.execution?.requirements?.join(', ') || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      execution: {
                        ...prev.execution || EMPTY_EXECUTION,
                        requirements: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
                      },
                    }))}
                    placeholder="如 pandas>=2.0, numpy>=1.24"
                    className="h-8 text-sm"
                  />
                </div>
              </div>
            )}

            {/* 正文 */}
            <div className="space-y-1.5">
              <Label>详细说明（SKILL.md 正文）</Label>
              <Textarea
                value={formData.body || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
                rows={4}
                placeholder="技能的详细使用说明..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFormDialogOpen(false)}>
              取消
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {isEditing ? '保存' : '创建'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 脚本文件上传对话框 */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>上传脚本文件</DialogTitle>
            <DialogDescription>
              为技能 <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">{uploadSkillName}</code> 上传脚本文件
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>文件名 *</Label>
              <Input
                value={uploadFilename}
                onChange={(e) => setUploadFilename(e.target.value)}
                placeholder="如 run.py 或 requirements.txt"
              />
              <p className="text-xs text-slate-500">不能上传 SKILL.md，文件名只允许字母、数字、下划线、点、连字符</p>
            </div>
            <div className="space-y-1.5">
              <Label>文件内容 *</Label>
              <Textarea
                value={uploadContent}
                onChange={(e) => setUploadContent(e.target.value)}
                rows={10}
                placeholder="粘贴文件内容..."
                className="font-mono text-sm"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsUploadDialogOpen(false)}>
              取消
            </Button>
            <Button onClick={handleUploadFile} disabled={isUploading}>
              {isUploading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              上传
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 删除确认对话框 */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除技能 <strong>{skillToDelete?.name}</strong> 吗？此操作将删除技能及其所有文件，且不可恢复。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
